import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { DetectionMode } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { AutoTrackingReservationStatePair } from './provider/index.js';
import { useDepot } from '#~/depot/hooks.js';

/**
 * Gets the auto tracking state for a specific camera.
 *
 * @param agentId - The id of the agent to get the auto tracking state for.
 * @param deviceId - The id of the camera to get the auto tracking state for.
 * @returns The auto tracking state for the camera.
 */
export const useAutoTracking = (
  agentId: T_Maybe<Agent['id']>,
  deviceId: Device['id']
): T_Maybe<AutoTrackingReservationStatePair> =>
  useDepot((µ) => (agentId ? µ.autoTrackingInstances[agentId]?.[deviceId] : undefined));

/**
 * Gets the auto tracking mode for a specific camera.
 *
 * @param agentId - The id of the agent to get the auto tracking mode for.
 * @param deviceId - The id of the camera to get the auto tracking mode for.
 * @returns The auto tracking mode for the camera.
 */
export const useAutoTrackingMode = (
  agentId: T_Maybe<Agent['id']>,
  deviceId: T_Maybe<Device['id']>
): T_Maybe<DetectionMode> =>
  useDepot((µ) =>
    deviceId && agentId
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        µ.autoTrackingInstances[agentId]?.[deviceId]?.state?.status?.detectionMode
      : undefined
  );
